import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { Empty, ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Drawer, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';

interface Metal {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  sort_order: number;
  is_active: boolean;
  version: number;
  melt_loss_pct?: string | null;
  bullion_hsn?: string | null;
}

interface Purity {
  id: string;
  metal_id: string;
  code: string;
  name: string;
  fineness: string;
  percent: string;
  karat: string | null;
  is_default_for_sale: boolean;
  is_default_for_purchase: boolean;
  sort_order: number;
  is_active: boolean;
  version: number;
}

/**
 * The metals a shop deals in and the purities it sells them at.
 *
 * These decide what can be rated, tagged and sold, so they belong to the shop
 * rather than to the software: a silver-only shop should not carry gold, and a
 * shop that sells 20K and 23K should be able to add them without asking anyone.
 *
 * Purity is stated once, as fineness — 916 parts in a thousand — and the karat
 * and percent follow from it rather than being typed three times and disagreeing.
 */
export function MetalsPage() {
  const { can } = usePermission();
  const navigate = useNavigate();
  const [metalDrawer, setMetalDrawer] = useState<Metal | 'new' | null>(null);
  const [purityDrawer, setPurityDrawer] = useState<Purity | { metalId: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [deleting, setDeleting] = useState<{ kind: 'metal'; row: Metal } | { kind: 'purity'; row: Purity } | null>(
    null,
  );
  const client = useQueryClient();
  const toast = useToast();

  // Delete removes an entry nobody has used; one already on a rate, tag or bill
  // is hidden instead, so old bills still read correctly.
  const remove = useMutation({
    mutationFn: (d: NonNullable<typeof deleting>) =>
      api.del<{ outcome: 'DELETED' | 'RETIRED'; usedBy?: string }>(
        `/${d.kind === 'metal' ? 'metals' : 'purities'}/${d.row.id}`,
        'Deleted from Metals and purities',
      ),
    onSuccess: (r, d) => {
      const label =
        d.kind === 'metal'
          ? d.row.name
          : d.row.name.includes(d.row.code)
            ? d.row.name
            : `${d.row.code} (${d.row.name})`;
      if (r.outcome === 'DELETED') toast(`${label} deleted`);
      else
        toast(
          `${label} is hidden, not deleted`,
          `It is already used by ${r.usedBy ?? 'existing records'}, so old bills keep it. It is no longer offered for new rates and tags.`,
        );
      setDeleting(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['metals'] });
      void client.invalidateQueries({ queryKey: ['purities'] });
      void client.invalidateQueries({ queryKey: ['masters'] });
    },
    onError: (e) => {
      setDeleting(null);
      setError(e);
    },
  });

  // Bring a hidden entry back into use.
  const restore = useMutation({
    mutationFn: (d: { kind: 'metal'; row: Metal } | { kind: 'purity'; row: Purity }) =>
      api.patch(`/${d.kind === 'metal' ? 'metals' : 'purities'}/${d.row.id}`, { is_active: true }, d.row.version),
    onSuccess: (_r, d) => {
      toast(`${d.kind === 'metal' ? d.row.name : d.row.code} is back in use`);
      void client.invalidateQueries({ queryKey: ['metals'] });
      void client.invalidateQueries({ queryKey: ['purities'] });
    },
    onError: setError,
  });
  const mayDelete = can('document.void');

  const metals = useQuery({ queryKey: ['metals'], queryFn: () => api.get<{ data: Metal[] }>('/metals?limit=50') });
  const purities = useQuery({
    queryKey: ['purities'],
    queryFn: () => api.get<{ data: Purity[] }>('/purities?limit=200'),
  });

  const mayEdit = can('master.manage') || can('master.create');
  const rows = metals.data?.data ?? [];
  const all = purities.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Metals and purities"
        subtitle="What this shop deals in. Rates, tags and bills all price against these."
        actions={
          <>
            <button className="btn-ghost" onClick={() => navigate('/rates')}>
              Today’s rates
            </button>
            {mayEdit ? (
              <button className="btn-primary" onClick={() => setMetalDrawer('new')}>
                Add metal
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={error ?? metals.error ?? purities.error} />

      {metals.isLoading ? <Loading rows={3} /> : null}
      {!metals.isLoading && rows.length === 0 ? (
        <Empty message="No metals set up yet." hint="Add gold or silver to begin." />
      ) : null}

      <div className="space-y-5">
        {rows.map((m) => {
          const mine = all.filter((p) => p.metal_id === m.id).sort((a, b) => a.sort_order - b.sort_order);
          return (
            <section
              key={m.id}
              className={`overflow-hidden rounded-xl border bg-surface shadow-card ${m.is_active ? 'border-silver-100' : 'border-dashed border-silver-400 opacity-70'}`}
            >
              <header className="flex flex-wrap items-center gap-3 border-b border-silver-100 bg-gold-50/50 px-4 py-3">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-gold-600 text-sm font-bold text-white">
                  {m.symbol || m.code.slice(0, 2)}
                </span>
                <div>
                  <p className="font-semibold">{m.name}</p>
                  <p className="num text-2xs text-ink-faint">
                    {m.code} · {mine.filter((p) => p.is_active).length} purities
                  </p>
                </div>
                {!m.is_active ? (
                  <span className="rounded bg-silver-100 px-1.5 py-0.5 text-2xs text-ink-soft">hidden</span>
                ) : null}
                <div className="ml-auto flex items-center gap-2">
                  {mayDelete && !m.is_active ? (
                    <button
                      className="btn-quiet text-2xs"
                      disabled={restore.isPending}
                      onClick={() => restore.mutate({ kind: 'metal', row: m })}
                    >
                      Bring back
                    </button>
                  ) : null}
                  {mayDelete && m.is_active ? (
                    <button
                      className="btn-quiet text-2xs text-danger"
                      onClick={() => setDeleting({ kind: 'metal', row: m })}
                    >
                      Delete metal
                    </button>
                  ) : null}
                  {mayEdit ? (
                    <>
                      <button className="btn-quiet text-2xs" onClick={() => setMetalDrawer(m)}>
                        Edit metal
                      </button>
                      <button className="btn-ghost py-1 text-2xs" onClick={() => setPurityDrawer({ metalId: m.id })}>
                        Add purity
                      </button>
                    </>
                  ) : null}
                </div>
              </header>

              {mine.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-ink-soft">
                  No purities yet. A gold shop usually has 916 (22K), 750 (18K) and 995 or 999 for bullion.
                </p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="th">Purity</th>
                      <th className="th">Name</th>
                      <th className="th text-right">Karat</th>
                      <th className="th text-right">Fineness</th>
                      <th className="th text-right">Percent</th>
                      <th className="th">Used by default for</th>
                      <th className="th" />
                    </tr>
                  </thead>
                  <tbody>
                    {mine.map((p) => (
                      <tr key={p.id} className={p.is_active ? '' : 'opacity-60'}>
                        <td className="td num font-medium">{p.code}</td>
                        <td className="td">{p.name}</td>
                        <td className="td num text-right">{p.karat ? `${Number(p.karat)}K` : '—'}</td>
                        <td className="td num text-right">{Number(p.fineness).toFixed(3)}</td>
                        <td className="td num text-right">{Number(p.percent).toFixed(2)}%</td>
                        <td className="td text-2xs">
                          {[p.is_default_for_sale ? 'selling' : '', p.is_default_for_purchase ? 'buying old gold' : '']
                            .filter(Boolean)
                            .join(' · ') || <span className="text-ink-faint">—</span>}
                        </td>
                        <td className="td whitespace-nowrap text-right">
                          {!p.is_active ? <span className="mr-3 text-2xs text-ink-soft">hidden</span> : null}
                          {mayEdit ? (
                            <button className="text-2xs text-gold-700 underline" onClick={() => setPurityDrawer(p)}>
                              Edit
                            </button>
                          ) : null}
                          {mayDelete && p.is_active ? (
                            <button
                              className="ml-4 text-2xs text-danger underline"
                              onClick={() => setDeleting({ kind: 'purity', row: p })}
                            >
                              Delete
                            </button>
                          ) : null}
                          {mayDelete && !p.is_active ? (
                            <button
                              className="ml-4 text-2xs text-gold-700 underline"
                              disabled={restore.isPending}
                              onClick={() => restore.mutate({ kind: 'purity', row: p })}
                            >
                              Bring back
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!deleting}
        tone="danger"
        title={
          deleting?.kind === 'metal'
            ? `Delete ${deleting.row.name}?`
            : deleting
              ? `Delete purity ${deleting.row.code}?`
              : ''
        }
        message={
          deleting?.kind === 'metal'
            ? `If ${deleting.row.name} has never been used, it is removed for good${((c) => (c > 0 ? `, with its ${c} ${c === 1 ? 'purity' : 'purities'}` : ''))(all.filter((p) => p.metal_id === deleting.row.id).length)}. If any rate, tag, bill or melt already uses it, it is hidden instead: old bills still show it, and it is no longer offered for anything new.`
            : 'If this purity has never been used, it is removed for good. If any product, rate, tag or bill already uses it, it is hidden instead: old bills still show it, and it is no longer offered for anything new.'
        }
        confirmLabel="Delete"
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
      />

      {metalDrawer ? (
        <MetalDrawer
          metal={metalDrawer === 'new' ? null : metalDrawer}
          onClose={() => setMetalDrawer(null)}
          onError={setError}
        />
      ) : null}
      {purityDrawer ? (
        <PurityDrawer
          purity={'id' in purityDrawer ? purityDrawer : null}
          metalId={'id' in purityDrawer ? purityDrawer.metal_id : purityDrawer.metalId}
          metals={rows}
          onClose={() => setPurityDrawer(null)}
        />
      ) : null}
    </>
  );
}

function MetalDrawer({
  metal,
  onClose,
  onError,
}: {
  metal: Metal | null;
  onClose: () => void;
  onError: (e: unknown) => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const [f, setF] = useState({
    code: metal?.code ?? '',
    name: metal?.name ?? '',
    symbol: metal?.symbol ?? '',
    sort_order: String(metal?.sort_order ?? 0),
    is_active: metal?.is_active ?? true,
    melt_loss_pct: metal?.melt_loss_pct ? String(Number(metal.melt_loss_pct)) : '',
    bullion_hsn: metal?.bullion_hsn ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: f.code.trim().toUpperCase(),
        name: f.name.trim(),
        ...(f.symbol.trim() ? { symbol: f.symbol.trim() } : {}),
        sort_order: Number(f.sort_order || 0),
        is_active: f.is_active,
        melt_loss_pct: f.melt_loss_pct.trim() ? f.melt_loss_pct.trim().replace(/\.$/, '') : null,
        bullion_hsn: f.bullion_hsn.trim() || null,
      };
      return metal ? api.patch(`/metals/${metal.id}`, body, metal.version) : api.post('/metals', body);
    },
    onSuccess: () => {
      toast(metal ? `${f.name} updated` : `${f.name} added`);
      void client.invalidateQueries({ queryKey: ['metals'] });
      onClose();
    },
    onError: (e) => {
      setError(e);
      onError(e);
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (f.code.trim() && f.name.trim()) save.mutate();
  };

  return (
    <Drawer
      title={metal ? metal.name : 'Add a metal'}
      subtitle="Gold, silver, platinum — whatever this shop deals in"
      onClose={onClose}
      footer={
        <button
          className="btn-primary w-full"
          disabled={!f.code.trim() || !f.name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : metal ? 'Save changes' : 'Add metal'}
        </button>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <Field label="Name" required>
            <TextInput
              value={f.name}
              onChange={(v) => setF({ ...f, name: v })}
              placeholder="Gold"
              maxLength={60}
              autoFocus
            />
          </Field>
          <Field label="Code" required hint="Never changes">
            <TextInput
              value={f.code}
              onChange={(v) => setF({ ...f, code: v })}
              uppercase
              maxLength={16}
              allow={/[A-Z0-9_]/}
              placeholder="GOLD"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Short symbol" hint="Shown on the rate board">
            <TextInput value={f.symbol} onChange={(v) => setF({ ...f, symbol: v })} maxLength={8} placeholder="Au" />
          </Field>
          <Field label="Order in lists">
            <NumberInput value={f.sort_order} onChange={(v) => setF({ ...f, sort_order: v })} decimals={0} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Usual melting loss" hint="Above this a melt needs approval. Blank = 1.5%">
            <NumberInput
              value={f.melt_loss_pct}
              onChange={(v) => setF({ ...f, melt_loss_pct: v })}
              decimals={4}
              max={100}
              suffix="%"
              placeholder="1.5"
            />
          </Field>
          <Field label="HSN for bars" hint="7108 gold · 7106 silver · 7110 platinum">
            <TextInput
              value={f.bullion_hsn}
              onChange={(v) => setF({ ...f, bullion_hsn: v })}
              maxLength={8}
              allow={/[0-9]/}
              placeholder="7108"
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />
          In use — offered when tagging stock and setting rates
        </label>
      </form>
    </Drawer>
  );
}

function PurityDrawer({
  purity,
  metalId,
  metals,
  onClose,
}: {
  purity: Purity | null;
  metalId: string;
  metals: Metal[];
  onClose: () => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const [error, setError] = useState<unknown>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [f, setF] = useState({
    metal_id: purity?.metal_id ?? metalId,
    code: purity?.code ?? '',
    name: purity?.name ?? '',
    // Fineness is the one true number: 916 parts per thousand.
    parts: purity ? String(Math.round(Number(purity.fineness) * 1000)) : '',
    karat: purity?.karat ? String(Number(purity.karat)) : '',
    is_default_for_sale: purity?.is_default_for_sale ?? false,
    is_default_for_purchase: purity?.is_default_for_purchase ?? false,
    sort_order: String(purity?.sort_order ?? 0),
    is_active: purity?.is_active ?? true,
  });

  const parts = Number(f.parts || 0);
  const fineness = (parts / 1000).toFixed(5);
  const percent = (parts / 10).toFixed(4);
  // 24 karat is pure, so karat follows from the parts unless it is overridden.
  const impliedKarat = parts > 0 ? ((parts / 1000) * 24).toFixed(2) : '';

  const save = useMutation({
    mutationFn: () => {
      const base = {
        code: f.code.trim().toUpperCase(),
        name: f.name.trim(),
        karat: f.karat ? Number(f.karat).toFixed(2) : null,
        is_default_for_sale: f.is_default_for_sale,
        is_default_for_purchase: f.is_default_for_purchase,
        sort_order: Number(f.sort_order || 0),
        is_active: f.is_active,
      };
      return purity
        ? api.patch(`/purities/${purity.id}`, base, purity.version)
        : api.post('/purities', { ...base, metal_id: f.metal_id, fineness, percent, karat: base.karat ?? undefined });
    },
    onSuccess: () => {
      toast(purity ? `${f.code} updated` : `${f.code} added`);
      void client.invalidateQueries({ queryKey: ['purities'] });
      onClose();
    },
    onError: setError,
  });

  const ready = f.code.trim() && f.name.trim() && (purity ? true : parts > 0 && parts <= 1000);

  return (
    <Drawer
      title={purity ? `${purity.code} — ${purity.name}` : 'Add a purity'}
      subtitle={metals.find((m) => m.id === f.metal_id)?.name ?? ''}
      onClose={onClose}
      footer={
        <button className="btn-primary w-full" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : purity ? 'Save changes' : 'Add purity'}
        </button>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => e.preventDefault()} className="space-y-3">
        {!purity ? (
          <Field label="Metal" required>
            <Select
              value={f.metal_id}
              onChange={(v) => setF({ ...f, metal_id: v })}
              options={metals.filter((m) => m.is_active).map((m) => ({ value: m.id, label: m.name }))}
            />
          </Field>
        ) : null}

        <div className="grid grid-cols-[1fr_1fr] gap-3">
          <Field label="Purity code" required hint="What staff call it">
            <TextInput
              value={f.code}
              onChange={(v) => setF({ ...f, code: v })}
              uppercase
              maxLength={16}
              placeholder="916"
            />
          </Field>
          <Field label="Name" required>
            <TextInput value={f.name} onChange={(v) => setF({ ...f, name: v })} maxLength={60} placeholder="22 Karat" />
          </Field>
        </div>

        {!purity ? (
          <Field
            label="Parts per thousand"
            required
            hint="916 means 916 parts gold in 1000. Everything else follows from this."
          >
            <NumberInput
              value={f.parts}
              onChange={(v) => setF({ ...f, parts: v })}
              decimals={0}
              max={1000}
              placeholder="916"
            />
          </Field>
        ) : null}

        {parts > 0 ? (
          <div className="grid grid-cols-3 gap-2 rounded-lg border border-gold-300 bg-gold-50 p-3 text-center text-sm">
            <div>
              <p className="text-2xs text-ink-soft">Fineness</p>
              <p className="num font-semibold">{Number(fineness).toFixed(3)}</p>
            </div>
            <div>
              <p className="text-2xs text-ink-soft">Percent</p>
              <p className="num font-semibold">{Number(percent).toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-2xs text-ink-soft">Karat</p>
              <p className="num font-semibold">{f.karat || impliedKarat}K</p>
            </div>
          </div>
        ) : null}

        <Field label="Karat" hint="Left blank, it is worked out from the parts above.">
          <NumberInput
            value={f.karat}
            onChange={(v) => setF({ ...f, karat: v })}
            decimals={2}
            max={24}
            placeholder={impliedKarat}
          />
        </Field>

        <div className="space-y-2 rounded-lg border border-silver-100 p-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.is_default_for_sale}
              onChange={(e) => setF({ ...f, is_default_for_sale: e.target.checked })}
            />
            Chosen first when selling
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.is_default_for_purchase}
              onChange={(e) => setF({ ...f, is_default_for_purchase: e.target.checked })}
            />
            Chosen first when buying old gold
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Order in lists">
            <NumberInput value={f.sort_order} onChange={(v) => setF({ ...f, sort_order: v })} decimals={0} />
          </Field>
          <Field label="In use">
            <label className="flex h-9 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={f.is_active}
                onChange={(e) => (e.target.checked ? setF({ ...f, is_active: true }) : setConfirmOff(true))}
              />
              Offered on rates and tags
            </label>
          </Field>
        </div>
      </form>

      <ConfirmDialog
        open={confirmOff}
        tone="danger"
        title="Stop using this purity?"
        message="Stock already tagged at this purity keeps it, and old bills are untouched. It simply stops being offered on new rates and new tags."
        confirmLabel="Stop using it"
        onCancel={() => setConfirmOff(false)}
        onConfirm={() => {
          setF({ ...f, is_active: false });
          setConfirmOff(false);
        }}
      />
    </Drawer>
  );
}
